import api from './api'
import type {
  KnowledgeBase,
  KnowledgeBaseListResponse,
  KnowledgeBaseListParams,
  KnowledgeBaseCreateRequest,
  KnowledgeBaseCreateResponse,
  KnowledgeBaseUpdateRequest,
} from '@/schemas/knowledge-base'
import type {
  GoogleDriveSyncConfigRequest,
  GoogleDriveSyncConfigResponse,
  GoogleDriveSyncTriggerResponse,
} from '@/schemas/google-drive-sync'
import type {
  NotionSyncConfigRequest,
  NotionSyncConfigResponse,
  NotionSyncTriggerResponse,
} from '@/schemas/notion-sync'
import type {
  UrlSyncConfigRequest,
  UrlSyncConfigResponse,
  UrlSyncTriggerResponse,
} from '@/schemas/url-sync'
import type { SyncLogListResponse, SyncLogListParams } from '@/schemas/sync-log'
import type { AxiosRequestConfig } from 'axios'

export const knowledgeBaseService = {
  // List knowledge bases with pagination and filters
  async listKnowledgeBases(
    projectId: string,
    params?: KnowledgeBaseListParams,
    config?: AxiosRequestConfig
  ): Promise<KnowledgeBaseListResponse> {
    const response = await api.get<KnowledgeBaseListResponse>(
      `/projects/${projectId}/knowledge-bases`,
      { params, ...config }
    )
    return response.data
  },

  // Get a single knowledge base by ID
  async getKnowledgeBase(
    projectId: string,
    kbId: string,
    config?: AxiosRequestConfig
  ): Promise<KnowledgeBase> {
    const response = await api.get<KnowledgeBase>(
      `/projects/${projectId}/knowledge-bases/${kbId}`,
      config
    )
    return response.data
  },

  // Create a new knowledge base
  async createKnowledgeBase(
    projectId: string,
    data: KnowledgeBaseCreateRequest,
    config?: AxiosRequestConfig
  ): Promise<KnowledgeBaseCreateResponse> {
    const response = await api.post<KnowledgeBaseCreateResponse>(
      `/projects/${projectId}/knowledge-bases`,
      data,
      config
    )
    return response.data
  },

  // Update an existing knowledge base
  async updateKnowledgeBase(
    projectId: string,
    kbId: string,
    data: KnowledgeBaseUpdateRequest,
    config?: AxiosRequestConfig
  ): Promise<KnowledgeBase> {
    const response = await api.patch<KnowledgeBase>(
      `/projects/${projectId}/knowledge-bases/${kbId}`,
      data,
      config
    )
    return response.data
  },

  // Delete a knowledge base (soft delete)
  async deleteKnowledgeBase(
    projectId: string,
    kbId: string,
    config?: AxiosRequestConfig
  ): Promise<void> {
    await api.delete(`/projects/${projectId}/knowledge-bases/${kbId}`, config)
  },

  // === Google Drive Sync ===

  // Get Google Drive sync configuration
  async getGoogleDriveSyncConfig(
    projectId: string,
    kbId: string,
    config?: AxiosRequestConfig
  ): Promise<GoogleDriveSyncConfigResponse> {
    const response = await api.get<GoogleDriveSyncConfigResponse>(
      `/projects/${projectId}/knowledge-bases/${kbId}/sync/google-drive`,
      config
    )
    return response.data
  },

  // Configure Google Drive sync
  async configureGoogleDriveSync(
    projectId: string,
    kbId: string,
    data: GoogleDriveSyncConfigRequest,
    config?: AxiosRequestConfig
  ): Promise<GoogleDriveSyncConfigResponse> {
    const response = await api.put<GoogleDriveSyncConfigResponse>(
      `/projects/${projectId}/knowledge-bases/${kbId}/sync/google-drive`,
      data,
      config
    )
    return response.data
  },

  // Trigger manual Google Drive sync
  async triggerGoogleDriveSync(
    projectId: string,
    kbId: string,
    config?: AxiosRequestConfig
  ): Promise<GoogleDriveSyncTriggerResponse> {
    const response = await api.post<GoogleDriveSyncTriggerResponse>(
      `/projects/${projectId}/knowledge-bases/${kbId}/sync/google-drive/trigger`,
      {},
      config
    )
    return response.data
  },

  // === Notion Sync ===

  // Get Notion sync configuration
  async getNotionSyncConfig(
    projectId: string,
    kbId: string,
    config?: AxiosRequestConfig
  ): Promise<NotionSyncConfigResponse> {
    const response = await api.get<NotionSyncConfigResponse>(
      `/projects/${projectId}/knowledge-bases/${kbId}/sync/notion`,
      config
    )
    return response.data
  },

  // Configure Notion sync
  async configureNotionSync(
    projectId: string,
    kbId: string,
    data: NotionSyncConfigRequest,
    config?: AxiosRequestConfig
  ): Promise<NotionSyncConfigResponse> {
    const response = await api.put<NotionSyncConfigResponse>(
      `/projects/${projectId}/knowledge-bases/${kbId}/sync/notion`,
      data,
      config
    )
    return response.data
  },

  // Trigger manual Notion sync
  async triggerNotionSync(
    projectId: string,
    kbId: string,
    config?: AxiosRequestConfig
  ): Promise<NotionSyncTriggerResponse> {
    const response = await api.post<NotionSyncTriggerResponse>(
      `/projects/${projectId}/knowledge-bases/${kbId}/sync/notion/trigger`,
      {},
      config
    )
    return response.data
  },

  // === URL Sync ===

  // Get URL sync configuration
  async getUrlSyncConfig(
    projectId: string,
    kbId: string,
    config?: AxiosRequestConfig
  ): Promise<UrlSyncConfigResponse> {
    const response = await api.get<UrlSyncConfigResponse>(
      `/projects/${projectId}/knowledge-bases/${kbId}/sync/url`,
      config
    )
    return response.data
  },

  // Configure URL sync
  async configureUrlSync(
    projectId: string,
    kbId: string,
    data: UrlSyncConfigRequest,
    config?: AxiosRequestConfig
  ): Promise<UrlSyncConfigResponse> {
    const response = await api.put<UrlSyncConfigResponse>(
      `/projects/${projectId}/knowledge-bases/${kbId}/sync/url`,
      data,
      config
    )
    return response.data
  },

  // Trigger manual URL sync
  async triggerUrlSync(
    projectId: string,
    kbId: string,
    config?: AxiosRequestConfig
  ): Promise<UrlSyncTriggerResponse> {
    const response = await api.post<UrlSyncTriggerResponse>(
      `/projects/${projectId}/knowledge-bases/${kbId}/sync/url/trigger`,
      {},
      config
    )
    return response.data
  },

  // === Sync Logs ===

  // Get sync logs for a knowledge base
  async getSyncLogs(
    projectId: string,
    kbId: string,
    params?: SyncLogListParams,
    config?: AxiosRequestConfig
  ): Promise<SyncLogListResponse> {
    const response = await api.get<SyncLogListResponse>(
      `/projects/${projectId}/knowledge-bases/${kbId}/sync-logs`,
      { params, ...config }
    )
    return response.data
  },
}
